/*
 * What the theme keeps in localStorage, and the checks on a theme imported
 * as JSON.
 */
import { safeStorage } from '../../utils/safeStorage';
import { ThemeColors, ThemeConfig, ThemeMode, ThemeVariant, defaultTheme, paletteFor } from './themePalettes';

const THEME_STORAGE_KEY = 'theme-config';
/**
 * Set as soon as the user touches a theme control. While it is set, the
 * server-side defaults no longer overwrite the user's choice on load.
 */
const THEME_CUSTOMISED_KEY = 'ui-prefs-theme-customised';

export const systemPrefersDark = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.matchMedia) && window.matchMedia('(prefers-color-scheme: dark)').matches;

/**
 * The theme and the dark flag for the very first render, read synchronously
 * so the first paint already has the right colours (no light flash on top
 * of the data-theme attribute the index.html bootstrap set).
 */
export const readInitialTheme = (): { theme: ThemeConfig; isDark: boolean } => {
  try {
    const saved = safeStorage.getItem(THEME_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const mode: ThemeMode = parsed?.mode || 'light';
      const dark = mode === 'dark' || (mode === 'auto' && systemPrefersDark());
      return { theme: { ...defaultTheme, ...parsed, colors: paletteFor(dark) }, isDark: dark };
    }
  } catch {
    /* malformed or private mode: the defaults apply */
  }
  return { theme: defaultTheme, isDark: false };
};

export const saveTheme = (theme: ThemeConfig): void => {
  safeStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
};

export const clearSavedTheme = (): void => {
  safeStorage.removeItem(THEME_STORAGE_KEY);
  safeStorage.removeItem(THEME_CUSTOMISED_KEY);
};

export const markThemeCustomised = (): void => {
  try {
    safeStorage.setItem(THEME_CUSTOMISED_KEY, 'true');
  } catch {
    /* noop */
  }
};

export const isThemeCustomised = (): boolean => {
  try {
    return safeStorage.getItem(THEME_CUSTOMISED_KEY) === 'true';
  } catch {
    return false;
  }
};

const VALID_MODES: ReadonlySet<ThemeMode> = new Set(['light', 'dark', 'auto']);
const VALID_VARIANTS: ReadonlySet<ThemeVariant> = new Set(['default', 'compact', 'comfortable']);

/** CSS colour values are short and never contain injection characters: anything else is refused. */
const SAFE_COLOR_VALUE = /^[#a-zA-Z0-9.,()\s%-]{1,128}$/;

const REQUIRED_COLOR_KEYS: readonly (keyof ThemeColors)[] = [
  'primary', 'primaryLight', 'primaryDark',
  'bgPrimary', 'bgSecondary', 'bgTertiary', 'bgElevated', 'bgOverlay',
  'textPrimary', 'textSecondary', 'textTertiary', 'textMuted', 'textInverse',
  'borderPrimary', 'borderSecondary', 'borderFocus', 'borderError',
  'interactiveHover', 'interactiveActive', 'interactiveDisabled',
  'success', 'error', 'warning', 'info',
  'shadowSm', 'shadowMd', 'shadowLg', 'shadowXl',
];

/** A theme from an import, or null when any part of it is not what a theme looks like. */
export const validateImportedTheme = (input: unknown): ThemeConfig | null => {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  if (typeof obj.mode !== 'string' || !VALID_MODES.has(obj.mode as ThemeMode)) return null;
  if (typeof obj.variant !== 'string' || !VALID_VARIANTS.has(obj.variant as ThemeVariant)) return null;
  if (!obj.colors || typeof obj.colors !== 'object') return null;

  const colors = obj.colors as Record<string, unknown>;
  const safeColors = {} as ThemeColors;
  for (const key of REQUIRED_COLOR_KEYS) {
    const value = colors[key];
    if (typeof value !== 'string' || !SAFE_COLOR_VALUE.test(value)) return null;
    safeColors[key] = value;
  }
  return {
    mode: obj.mode as ThemeMode,
    variant: obj.variant as ThemeVariant,
    colors: safeColors,
    animations: typeof obj.animations === 'boolean' ? obj.animations : true,
    reducedMotion: typeof obj.reducedMotion === 'boolean' ? obj.reducedMotion : false,
  };
};
